import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  CheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const PortfolioPage: React.FC = () => {
  useSEO(pageConfigs.portfolio);
  const portfolioItems = [
    {
      id: 1,
      title: "Credit Management App",
      category: "Mobile Development",
      description: "Secure cross-platform credit management application with advanced security features and intuitive UX design. This app is being used by the shopkeepers and customers to track and manage their credit entries.",
      image: "https://play-lh.googleusercontent.com/yFHaFPqUMAb75MWz6W_Kl94_FP5kr3KzWwEm0Scn1JLiPqdfUP_d0PoeE-DP6572kA=w240-h480-rw",
      technologies: ["React Native", "Node.js", "MySQL", "AWS"],
      results: ["4.8+ star rating", "95% user retention", "Zero security breaches"],
      liveUrl: "https://mydone.in",
      caseStudyUrl: "#",
      icon: <DevicePhoneMobileIcon className="w-6 h-6" />
    },
    {
      id: 2,
      title: "E-commerce Platform Redesign",
      category: "Website Development",
      description: "Complete redesign and development of a high-traffic e-commerce platform with improved conversion rates.",
      image: "https://www.juhst.com/cdn/shop/files/clipboard-image-1752146282.png?v=1752146323&width=200",
      technologies: ["Shopify"],
      results: ["300% increase in conversions", "50% faster load times", "40% increase in mobile sales", "99.9% uptime"],
      liveUrl: "https://juhst.com",
      caseStudyUrl: "#",
      icon: <ComputerDesktopIcon className="w-6 h-6" />
    },
    {
      id: 3,
      title: "Vision3D Hologram Business",
      category: "Lead Generation",
      description: "Multi-location lead generation campaign for a restaurant chain with local SEO and targeted advertising.",
      image: "https://www.vision3d.in/assets/images/Logo3D.png",
      technologies: ["Google Ads", "Local SEO", "Google My Business"],
      results: ["150+ leads monthly", "25% increase in foot traffic", "4.9 star average rating", "200% ROI"],
      liveUrl: "https://vision3d.in",
      caseStudyUrl: "https://vision3d.in",
      icon: <MegaphoneIcon className="w-6 h-6" />
    },
    {
      id: 4,
      title: "Affvertize Affiliate Network",
      category: "Lead Generation",
      description: "Multi-location lead generation campaign for a restaurant chain with local SEO and targeted advertising.",
      image: "https://www.affvertize.com/assets/images/logo.png",
      technologies: ["Google Ads", "Meta Ads", "Display Network"],
      results: ["150K+ leads monthly", "Huge trafic", "CPI Offers", "200% ROI"],
      liveUrl: "https://affvertize.com",
      caseStudyUrl: "https://affvertize.com",
      icon: <MegaphoneIcon className="w-6 h-6" />
    },
    {
      id: 5,
      title: "MealBourne Meal Planner",
      category: "Website Development",
      description: "A Global Recipe creation and sharing web application.",
      image: "https://mealbourne.com/_next/image?url=%2Fassets%2Flogo.png&w=128&q=75",
      technologies: ["Next.js", "Node.js", "MySQL"],
      results: ["Latest Teck Stack", "Professional Website"],
      liveUrl: "https://mealbourne.com",
      caseStudyUrl: "#",
      icon: <MegaphoneIcon className="w-6 h-6" />
    }		
  ];

  const categories = ["All", "Mobile Development", "Website Development", "Lead Generation", "Digital Marketing", "Amazon Ads"];
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredItems = selectedCategory === "All" 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.portfolio} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
            <EyeIcon className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700 font-semibold text-sm">Our Work</span>
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
            Portfolio & 
            <span className="text-blue-600"> Case Studies</span>
          </h1>
          
          <p className="text-lg text-slate-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            Explore our successful projects and see how we've helped businesses achieve their goals 
            through innovative digital solutions and strategic marketing campaigns.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  selectedCategory === category
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video overflow-hidden bg-slate-100 flex items-center justify-center">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                      <div className="text-slate-700">{item.icon}</div>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 mb-4">{item.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.technologies.map((tech, index) => (
                        <span key={index} className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Key Results:</h4>
                    <div className="space-y-2">
                      {item.results.slice(0, 2).map((result, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckIcon className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <a 
                      href={item.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>View Details</span>
                    </a>
                    <a 
                      href={item.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Portfolio Highlights
            </h2>
            <p className="text-lg text-slate-600">
              Numbers that showcase our commitment to delivering exceptional results
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">50+</div>
              <div className="text-slate-600">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">98%</div>
              <div className="text-slate-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">300%</div>
              <div className="text-slate-600">Average ROI Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">24/7</div>
              <div className="text-slate-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-2xl lg:text-3xl font-display font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create a solution that delivers exceptional results for your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>Start Your Project</span>
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
            <Link 
              to="/contact"
              className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PortfolioPage;